import { x } from '@xstyled/styled-components';
import React, { RefObject, useRef, useState } from 'react';
import { t } from 'ttag';
import { useFocusTrap } from '~/lib/a11y/useFocusTrap';
import { uniqueId } from '~/lib/uniqueId';
import { Flex } from '~/ui/flex/Flex';
import { LlamaModal } from '~/ui/llama-modal/LlamaModal';
import { Overlay } from '~/ui/overlay/Overlay';
import { PretestButton, TextButton } from './PretestScreen';

/**
 * Leaving mid-assessment: keep going, or skip the assessment and take the course. Nothing is graded until
 * submit, so skipping only gives up the chance to test out in this attempt. While the choice is on its
 * way (busy), neither choice nor Escape can take it back.
 */
export const PretestExitDialog = ({
  open,
  onStay,
  onSkip,
  busy,
  returnFocusRef
}: {
  open: boolean;
  onStay: () => void;
  onSkip: () => void;
  busy: boolean;
  returnFocusRef: RefObject<HTMLElement>;
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const stayRef = useRef<HTMLButtonElement>(null);
  const [titleId] = useState(() => uniqueId('pretest-exit-title'));
  const [descriptionId] = useState(() => uniqueId('pretest-exit-description'));
  useFocusTrap(dialogRef, open, stayRef, returnFocusRef);

  if (!open) return null;

  return (
    <Overlay open opacity={0.5}>
      <LlamaModal horizontal="center" vertical="center" allowScroll>
        <Flex
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === 'Escape' && !busy) onStay();
          }}
          w={{ _: '100%', sm: 'lg' }}
          maxW="100%"
          flexDirection="column"
          p="xl"
          gap="md"
        >
          <x.h2 id={titleId} fontFamily="llama" m={0}>
            {t`Leave the assessment?`}
          </x.h2>
          <x.p id={descriptionId} m={0}>
            {t`If you skip, it won’t be offered again in this attempt.`}
          </x.p>
          <Flex flexDirection="column" gap="sm" alignItems="center">
            <PretestButton
              ref={stayRef}
              w="100%"
              onClick={onStay}
              disabled={busy}
            >
              {t`Keep going`}
            </PretestButton>
            <TextButton onClick={onSkip} disabled={busy}>
              {t`Skip assessment and take the course`}
            </TextButton>
          </Flex>
        </Flex>
      </LlamaModal>
    </Overlay>
  );
};
